import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { catchError, of, Subject, switchMap, takeUntil } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { responseAtPath } from '../waterfall-chart/waterfall-chart.model';
import { buildInvestmentReturnSummary, extractSummaryRecords, SummaryValue } from './investment-return-summary.model';

@Component({
  selector: 'lib-investment-return-summary', standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  templateUrl: './investment-return-summary.component.html',
  styleUrl: './investment-return-summary.component.scss',
})
export class InvestmentReturnSummaryComponent implements OnInit, OnDestroy {
  @Input() data: any;
  model!: ReturnType<typeof buildInvestmentReturnSummary>;
  loading = true;
  error = false;
  private destroy$ = new Subject<void>();
  constructor(private config: ConfigService, private global: GlobalService) {}
  ngOnInit() {
    this.model = buildInvestmentReturnSummary(this.data, []);
    this.global.filters$.pipe(
      switchMap(filters => {
        this.loading = true; this.error = false;
        const fixed = Object.assign({}, ...(this.data.additionalFilters || []));
        const yearFilter = this.data.yearFilterKey ? { [this.data.yearFilterKey]: this.data.years.map((year: any) => year.value) } : {};
        return this.config.fetchData(this.data.apiUrl, this.data.functionName,
          { source: this.data.sources, ...filters?.filter, ...fixed, ...yearFilter }, this.data, 'post')
          .pipe(catchError(() => { this.error = true; return of(null); }));
      }), takeUntil(this.destroy$),
    ).subscribe(response => {
      const mapped = this.data.responsePath ? responseAtPath(response, this.data.responsePath) : null;
      const records = Array.isArray(mapped) ? mapped : extractSummaryRecords(response, this.data.respKey, this.data.fields);
      this.model = buildInvestmentReturnSummary(this.data, records);
      this.loading = false;
    });
  }
  refresh() { this.global.setFilters(this.global.getCurrentFilters()); }
  format(cell: SummaryValue) {
    if (cell.value === null) return this.data.labels.noValue;
    const number = new Intl.NumberFormat(this.data.numberFormat.locale, {
      minimumFractionDigits: this.data.numberFormat.minDecimals,
      maximumFractionDigits: this.data.numberFormat.decimals,
    }).format(cell.value);
    return number + (cell.partial ? this.data.labels.partialMarker : '');
  }
  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }
}
